import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { WordPopover } from '../WordPopover';

interface ClickableTextProps {
  text: string;
  onWordClick?: (word: string) => boolean | void; // Return false to delay popover
  className?: string;
  pendingWord?: string | null; // Word waiting for confirmation
}

export const ClickableText: React.FC<ClickableTextProps> = ({
  text,
  onWordClick,
  className = '',
  pendingWord = null
}) => {
  const [selectedWord, setSelectedWord] = useState<string | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number; maxHeight: number } | null>(null);
  const [pendingWordState, setPendingWordState] = useState<string | null>(null);
  const [pendingPosition, setPendingPosition] = useState<{ top: number; left: number; maxHeight: number } | null>(null);

  const cleanWord = (word: string) => {
    return word
      .replace(/'s\b/g, ' is')
      .replace(/'re\b/g, ' are')
      .replace(/'ve\b/g, ' have')
      .replace(/'d\b/g, ' would')
      .replace(/'ll\b/g, ' will')
      .replace(/'m\b/g, ' am')
      .replace(/―/g, '')
      .replace('...', '')
      .replace(/n't\b/g, ' not')
      .replace(/{\/?it}/g, '')
      .replace(/{\/?phrase}/g, '')
      .replace(/\bok\b/g, 'okay')
      .replace(/\bOK\b/g, 'okay')
      .replace(/\bcan't\b/g, 'cannot')
      .replace('=', '')
      .replace('[', '')
      .replace(']', '')
      .replace(';', '')
      .replace(/\bwon't\b/g, 'will not')
      .replace(/[.,!?]/g, '')
      .trim()
      .split(' ')[0];
  };

  const handleWordClick = (word: string, event: React.MouseEvent) => {
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    const cleanedWord = cleanWord(word);
    
    // Calculate available space below and above the clicked word
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const estimatedPopoverHeight = 400; // Estimated max height of popover
    const minPadding = 16; // Minimum padding from viewport edges
    
    let top: number;
    let maxHeight: number;
    
    // If there's not enough space below, position above the word
    if (spaceBelow < estimatedPopoverHeight && spaceAbove > spaceBelow) {
      top = rect.top - estimatedPopoverHeight - 8;
      // Calculate max height based on available space above
      maxHeight = Math.min(70 * viewportHeight / 100, rect.top - minPadding);
    } else {
      top = rect.bottom + 8;
      // Calculate max height based on available space below
      maxHeight = Math.min(70 * viewportHeight / 100, viewportHeight - rect.bottom - minPadding);
    }
    
    // Ensure popover doesn't go above viewport
    top = Math.max(minPadding, top);
    // Ensure maxHeight is at least reasonable
    maxHeight = Math.max(200, maxHeight);
    
    const position = {
      top,
      left: rect.left,
      maxHeight
    };
    
    // Call onWordClick first (may show confirmation modal)
    if (onWordClick) {
      const shouldShow = onWordClick(cleanedWord);
      // If onWordClick returns false, delay showing popover
      if (shouldShow === false) {
        setPendingWordState(cleanedWord);
        setPendingPosition(position);
        return;
      }
    }
    
    // Show popover immediately
    setSelectedWord(cleanedWord);
    setPopoverPosition(position);
  };

  // Show popover for pending word after confirmation
  useEffect(() => {
    if (pendingWord && pendingWord === pendingWordState && pendingPosition) {
      setSelectedWord(pendingWord);
      setPopoverPosition(pendingPosition);
      setPendingWordState(null);
      setPendingPosition(null);
    }
  }, [pendingWord, pendingWordState, pendingPosition]);

  const words = text.split(' ');
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <>
      <span className={className}>
        {words.map((word, i, arr) => {
          return (
            <span key={i}>
              <span
                className="cursor-pointer hover:underline hover:text-indigo-600 transition-colors"
                onClick={(e) => handleWordClick(word, e)}
              >
                {word}
              </span>
              {i < arr.length - 1 && ' '}
            </span>
          );
        })}
      </span>

      {selectedWord && popoverPosition && createPortal(
        <>
          <div 
            className="fixed inset-0 z-[9998] bg-black/20 md:hidden" 
            onClick={() => {
              setSelectedWord(null);
              setPopoverPosition(null);
            }} 
          />
          <div
            className="fixed z-[9999] max-md:left-1/2 max-md:top-1/2 max-md:-translate-x-1/2 max-md:-translate-y-1/2"
            style={
              isMobile 
                ? {
                    width: '90vw',
                    maxWidth: '320px',
                    maxHeight: '80vh',
                  }
                : {
                    left: `clamp(16px, ${popoverPosition.left}px, calc(100vw - 300px))`,
                    top: `${popoverPosition.top}px`,
                    maxWidth: '90vw',
                    maxHeight: `${popoverPosition.maxHeight}px`,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                  }
            }
          >
            <div className={`w-full h-full ${isMobile ? 'overflow-y-auto' : ''}`}>
              <WordPopover
                word={selectedWord}
                context={text}
                position={{ top: 0, left: 0 }}
                useRelativePosition={true}
                onClose={() => {
                  setSelectedWord(null);
                  setPopoverPosition(null);
                }}
              />
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
};

